/**
 * Advanced Artifact Search Component
 * Provides comprehensive search and filtering for artifacts
 */

import React, { useState } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { Search, X, Filter, Calendar, FileType, Database, HardDrive } from 'lucide-react';
import { searchArtifacts, SearchArtifactsParams } from '../../api/artifacts';
import { Artifact, DatabaseSource } from '../../types/api';
import { RegistryFilterBar, DEFAULT_REGISTRY_FILTERS } from './RegistryFilterBar';
import {
  applyRegistryFilters,
  getRegistryFilterOptions,
  hasRegistryMeta,
  type RegistryFilterOptions,
  type RegistryGroupBy,
} from '../../utils/registryArtifactUtils';

interface AdvancedArtifactSearchProps {
  projectId: string;
  onResults: (results: Artifact[], total: number) => void;
  onClose?: () => void;
  artifactTypes?: string[];
  sources?: DatabaseSource[];
}

export function AdvancedArtifactSearch({
  projectId,
  onResults,
  onClose,
  artifactTypes = [],
  sources = [],
}: AdvancedArtifactSearchProps) {
  const [query, setQuery] = useState('');
  const [artifactType, setArtifactType] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minSizeMb, setMinSizeMb] = useState('');
  const [maxSizeMb, setMaxSizeMb] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registryFilters, setRegistryFilters] = useState(DEFAULT_REGISTRY_FILTERS);
  const [groupBy, setGroupBy] = useState<RegistryGroupBy>('none');
  const [registryOptions, setRegistryOptions] = useState<RegistryFilterOptions>({
    projectIds: [],
    tags: [],
    docTypes: [],
    clients: [],
    geographies: [],
    serviceLines: [],
    registrySources: [],
  });
  const [registryArtifactCount, setRegistryArtifactCount] = useState(0);

  const handleSearch = async () => {
    setSearching(true);
    setError(null);

    try {
      const params: SearchArtifactsParams = {
        project_id: projectId,
        limit: 1000, // Get more results for advanced search
      };

      if (query) params.query = query;
      if (artifactType) params.artifact_type = artifactType;
      if (sourceId) params.source_id = sourceId;
      if (sourceType) params.source_type = sourceType;
      if (metadataKey) {
        params.metadata_key = metadataKey;
        if (metadataValue) params.metadata_value = metadataValue;
      }
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (minSizeMb) params.min_size_mb = parseFloat(minSizeMb);
      if (maxSizeMb) params.max_size_mb = parseFloat(maxSizeMb);

      const response = await searchArtifacts(params);
      
      if (response.error) {
        setError(response.error);
      } else {
        setRegistryOptions(getRegistryFilterOptions(response.artifacts));
        setRegistryArtifactCount(response.artifacts.filter(hasRegistryMeta).length);
        const filtered = applyRegistryFilters(response.artifacts, registryFilters);
        onResults(filtered, filtered.length);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setArtifactType('');
    setSourceId('');
    setSourceType('');
    setMetadataKey('');
    setMetadataValue('');
    setDateFrom('');
    setDateTo('');
    setMinSizeMb('');
    setMaxSizeMb('');
    setError(null);
  };

  const activeFilterCount = [
    query,
    artifactType,
    sourceId,
    sourceType,
    metadataKey,
    dateFrom,
    dateTo,
    minSizeMb,
    maxSizeMb,
  ].filter(Boolean).length;

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Search className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Advanced Search</h3>
              <p className="text-sm text-gray-600">
                Search across {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Main Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Query
          </label>
          <Input
            type="text"
            placeholder="Search in filename, storage path, or metadata..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            leftIcon={<Search className="h-4 w-4 text-gray-400" />}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          <p className="text-xs text-gray-500 mt-1">
            Tip: Search for keywords like "proposal", "RFP", client names, or service lines
          </p>
        </div>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Artifact Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileType className="h-4 w-4 inline mr-1" />
              Artifact Type
            </label>
            <select
              value={artifactType}
              onChange={(e) => setArtifactType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Types</option>
              {artifactTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Source Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Database className="h-4 w-4 inline mr-1" />
              Source Type
            </label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Source Types</option>
              <option value="unc">UNC (Network Storage)</option>
              <option value="upload">Direct Upload</option>
              <option value="api_integration_*">API Integration (Any)</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date From
            </label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date To
            </label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          {/* Min Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <HardDrive className="h-4 w-4 inline mr-1" />
              Min Size (MB)
            </label>
            <Input
              type="number"
              placeholder="0.5"
              value={minSizeMb}
              onChange={(e) => setMinSizeMb(e.target.value)}
              step="0.1"
            />
          </div>

          {/* Max Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <HardDrive className="h-4 w-4 inline mr-1" />
              Max Size (MB)
            </label>
            <Input
              type="number"
              placeholder="50"
              value={maxSizeMb}
              onChange={(e) => setMaxSizeMb(e.target.value)}
              step="0.1"
            />
          </div>
        </div>

        {/* Metadata Filters */}
        <div className="border-t border-blue-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Metadata Filters</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metadata Key
              </label>
              <Input
                type="text"
                placeholder="e.g., win_status, service_line"
                value={metadataKey}
                onChange={(e) => setMetadataKey(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Common keys: win_status, service_line, client_name, project_name
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metadata Value
              </label>
              <Input
                type="text"
                placeholder="e.g., Won, Biology"
                value={metadataValue}
                onChange={(e) => setMetadataValue(e.target.value)}
                disabled={!metadataKey}
              />
            </div>
          </div>
        </div>

        {registryArtifactCount > 0 && (
          <RegistryFilterBar
            filters={registryFilters}
            options={registryOptions}
            groupBy={groupBy}
            registryArtifactCount={registryArtifactCount}
            onFiltersChange={setRegistryFilters}
            onGroupByChange={setGroupBy}
            onClear={() => setRegistryFilters(DEFAULT_REGISTRY_FILTERS)}
          />
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button onClick={handleSearch} disabled={searching} className="flex-1">
            {searching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search
              </>
            )}
          </Button>
          <Button variant="outline" onClick={handleClear} disabled={searching}>
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>

        {/* Quick Search Examples */}
        <div className="border-t border-blue-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Searches:</h4>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setQuery('proposal');
                handleSearch();
              }}
            >
              Proposals
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setQuery('RFP');
                handleSearch();
              }}
            >
              RFPs
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDateFrom('2023-01-01');
                setDateTo('2026-12-31');
                handleSearch();
              }}
            >
              2023-2026
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMinSizeMb('0.5');
                setMaxSizeMb('50');
                handleSearch();
              }}
            >
              0.5-50 MB
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

